import React from 'react';
import classnames from 'classnames';
import invert from 'lodash/invert';

import IconButton from 'eventbrite_design_system/iconButton/IconButton';
import CrossChunkySvg from 'eventbrite_design_system/iconography/icons/CrossChunky';

import FilterSection from './FilterSection';
import { gettext } from '../../translations';
import {
    CUSTOM_DATE,
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    EVENTBRITE_CATEGORY_MAPPING_SEARCH,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    DATES_MAPPING_SEARCH,
    DATES_TO_REFINEMENT,
    PRICE_MAPPING,
} from '../constants';
import withStickyScrollNewLayout from './withStickyScrollNewLayout';

import './FilterPanel.scss';

const FilterTitleDesktop = ({filterTitle, resetLink, selectedFilterCount}) => (
    <header className="filter-title-container">
        <div className="filter-title">{filterTitle}</div>
        {
            selectedFilterCount ? (
                <a
                    className="filter-count-link eds-text-weight--heavy"
                    href={resetLink}
                >
                    {gettext('Clear')}
                    <div className="filter-count eds-text-color--white eds-text-bs eds-l-mar-left-2">
                        {selectedFilterCount}
                    </div>
                </a>
            ) : null
        }
    </header>
);

const FilterTitleMobile = ({filterTitle, resetLink, selectedFilterCount, openCloseFilterPanelMobile}) => (
    <header className="filter-title-container--mobile">
        <a
            className={
                classnames(
                    'filter-count-link',
                    'eds-text-weight--heavy',
                    {'filter-count-link--is-hidden': !selectedFilterCount}
                )
            }
            href={resetLink}
        >
            {gettext('Clear')}
            <div className="filter-count eds-text-color--white eds-text-bs eds-l-mar-left-2">
                {selectedFilterCount}
            </div>
        </a>
        <div className="filter-title filter-title--mobile">
            <div>{filterTitle}</div>
        </div>
        <IconButton
            iconType={<CrossChunkySvg />}
            title={gettext('Close')}
            onClick={() => openCloseFilterPanelMobile(false)}
        />
    </header>
);

export class FilterPanelBase extends React.PureComponent {
    _handleChange = (key, value, shouldUpdateRoute) => {
        const {
            handleApplyFilter,
            handleRemoveFilter,
            isMobile,
            openCloseFilterPanelMobile,
            filterTypeToShow,
        } = this.props;

        if (value === 'all') {
            handleRemoveFilter({ [key]: true });
        } else {
            handleApplyFilter({ [key]: value }, {}, shouldUpdateRoute);
        }

        if (isMobile && filterTypeToShow) {
            // If the mobile view is opened to only one type of filter (date, category, etc),
            // then close it immediately after selection.
            // If the full list is open we want to keep it open.
            openCloseFilterPanelMobile(false);
        }

        // Scroll back to top of screen, otherwise
        // it looks strange
        window.scrollTo(0, 0);
    };

    _handleCustomDate = (startDate, endDate) => {
        const { handleApplyFilter } = this.props;

        handleApplyFilter({ dates: CUSTOM_DATE }, { startDate, endDate });
    };

    // eslint-disable-next-line complexity
    render() {
        const {isMobile, filterTypeToShow} = this.props;

        let filterTitle = gettext('Filters');

        if (filterTypeToShow) {
            if (filterTypeToShow === 'dates') {
                filterTitle = gettext('Date');
            }

            if (filterTypeToShow === 'tags') {
                filterTitle = gettext('Category');
            }

            if (filterTypeToShow === 'format') {
                filterTitle = gettext('Format');
            }

            if (filterTypeToShow === 'price') {
                filterTitle = gettext('Price');
            }
        }

        const filterTitleContainer = isMobile ? (
            <FilterTitleMobile
                filterTitle={filterTitle}
                selectedFilterCount={this.props.selectedFilterCount}
                resetLink={this.props.resetLink}
                openCloseFilterPanelMobile={this.props.openCloseFilterPanelMobile}
            />
        ) : (
            <FilterTitleDesktop
                filterTitle={filterTitle}
                selectedFilterCount={this.props.selectedFilterCount}
                resetLink={this.props.resetLink}
            />
        );

        const moreThreshold = filterTypeToShow ? undefined : 5;

        return (
            <div
                className={classnames('filter-panel-and-title-container', {
                    'filter-panel-and-title-container--mobile': isMobile,
                })}
            >
                {filterTitleContainer}
                <section className={classnames('filter-panel', {'filter-panel--mobile': isMobile})}>
                    {
                        (!filterTypeToShow || (filterTypeToShow && filterTypeToShow === 'dates')) &&
                        <FilterSection
                            title={gettext('Date')}
                            shouldHideTitle={!!filterTypeToShow}
                            filters={{'all': gettext('Any day'), ...DATES_MAPPING_SEARCH}}
                            onOptionChange={(value, redirect) => this._handleChange('dates', value, redirect)}
                            onCustomDate={(startDate, endDate) => this._handleCustomDate(startDate, endDate)}
                            urlParameterMapping={invert(DATES_TO_REFINEMENT)}
                            moreLabel={gettext('More dates')}
                            lessLabel={gettext('Fewer dates')}
                            moreThreshold={moreThreshold}
                            isDateSection={true}
                            filterType="date"
                        />
                    }
                    {
                        (!filterTypeToShow || (filterTypeToShow && filterTypeToShow === 'tags')) &&
                        <FilterSection
                            title={gettext('Category')}
                            shouldHideTitle={!!filterTypeToShow}
                            filters={{'all': gettext('Any category'), ...EVENTBRITE_CATEGORY_MAPPING_SEARCH}}
                            onOptionChange={(value, redirect) => this._handleChange('tags', value, redirect)}
                            urlParameterMapping={invert(EVENTBRITE_CATEGORY_TO_REFINEMENT)}
                            moreLabel={gettext('More categories')}
                            lessLabel={gettext('Fewer categories')}
                            moreThreshold={moreThreshold}
                            filterType="category"
                        />
                    }
                    {
                        (!filterTypeToShow || (filterTypeToShow && filterTypeToShow === 'format')) &&
                        <FilterSection
                            title={gettext('Format')}
                            shouldHideTitle={!!filterTypeToShow}
                            filters={{'all': gettext('Any format'), ...EVENTBRITE_FORMAT_MAPPING_SEARCH}}
                            onOptionChange={(value, redirect) => this._handleChange('format', value, redirect)}
                            urlParameterMapping={invert(EVENTBRITE_FORMAT_TO_REFINEMENT)}
                            moreLabel={gettext('More formats')}
                            lessLabel={gettext('Fewer formats')}
                            moreThreshold={moreThreshold}
                            filterType="format"
                        />
                    }
                    {
                        (!filterTypeToShow || (filterTypeToShow && filterTypeToShow === 'price')) &&
                        <div className="eds-l-mar-bot-20">
                            <FilterSection
                                title={gettext('Price')}
                                shouldHideTitle={!!filterTypeToShow}
                                filters={{'all': gettext('Any price'), ...PRICE_MAPPING}}
                                onOptionChange={(value, redirect) => this._handleChange('price', value, redirect)}
                                filterType="price"
                            />
                        </div>
                    }
                </section>
            </div>
        );
    }
}

export default withStickyScrollNewLayout(FilterPanelBase);
